import { arrayOf, object, string } from 'prop-types';
import React, { Fragment } from 'react';
import Head from 'next/head';
import { GA_ID, FACEBOOK_PIXEL_ID, PARSELY_SITE } from 'constants/vendor';
import { getGaPageType } from 'util/ga';
import { extractGtmMetadataFromJsonLd } from 'util/gtm';
import { extractMetadataFromBundleItems } from 'util/metadata';

const FB_PIXEL_SCRIPT = `
{
  "vars": {
    "pixelId": ${FACEBOOK_PIXEL_ID}
  },
  "triggers": {
    "trackPageview": {
      "on": "visible",
      "request": "pageview"
    }
  }
}`;

const PARSELY_SCRIPT = `
{
  "vars": {
    "apikey": "${PARSELY_SITE}"
  }
}`;

export const getGAConfig = (metadataItems, pageType, triggers) => {
  const parsed = extractMetadataFromBundleItems(metadataItems);
  const md = extractGtmMetadataFromJsonLd(parsed.jsonLd);
  return JSON.stringify({
    vars: {
      account: GA_ID,
      // we don't want the site suffix in the page title sent to GA
      title: parsed.title
    },
    extraUrlParams: {
      cd1: md.author || null,
      cd2: (md.categories && md.categories.join(', ')) || null,
      cd3: md.datePublished || null,
      cd26: md.dateModified || null,
      cd42: md.dateRepromoted || null,
      cd43: md.dateUpdated || null,
      // tags are joined with spaces to match GTM config
      cd7: (md.tags && md.tags.join(', ')) || null,
      cd17: (md.knowledgeTags && md.knowledgeTags.join(', ')) || null,
      cd11: 'gen-amp',
      // template is parsed client-side by AMP JS
      // eslint-disable-next-line no-template-curly-in-string
      cd45: '${ampdocHost}',
      cd47: parsed.recipeTaxonomy || null,
      cd48: (md.teams && md.teams.join(', ')) || null,
      cg1: getGaPageType(pageType) || null
    },
    triggers: {
      trackPageview: {
        on: 'visible',
        request: 'pageview'
      },
      ...triggers
    }
  });
};

function AmpAnalytics({ metadataItems, pageType, triggers }) {
  return (
    <Fragment>
      <Head>
        <script
          async
          key="amp-analytics"
          custom-element="amp-analytics"
          src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
        />
      </Head>

      {/* GTM */}
      {/* Loads Comscore and Quantcast Measure */}
      <amp-analytics
        config="https://www.googletagmanager.com/amp.json?id=GTM-N8BZ9N7&gtm.url=SOURCE_URL"
        data-credentials="include"
      />

      {/* Google Analytics */}
      {/*
        NOTE: It's possible to use GTM for GA and AMP vars to set dimensions.
        However, it is not possible to set "content groups" in the AMP GA tag.
      */}
      <amp-analytics type="googleanalytics" data-credentials="include">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: getGAConfig(metadataItems, pageType, triggers)
          }}
        />
      </amp-analytics>

      {/* FB Pixel */}
      <amp-analytics type="facebookpixel" id="facebook-pixel">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: FB_PIXEL_SCRIPT
          }}
        />
      </amp-analytics>

      {/* Parsely */}
      {/*
        NOTE: Parsely can be implemented using GTM however pings must be fired
        manually. While this is possible using a GTM timer, there appears to be
        no way to set the `inc` value - consequently the required `inc` value is
        empty when the heartbeat pixel is fired.
      */}
      <amp-analytics type="parsely">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: PARSELY_SCRIPT
          }}
        />
      </amp-analytics>
    </Fragment>
  );
}

AmpAnalytics.propTypes = {
  pageType: string.isRequired,
  metadataItems: arrayOf(object).isRequired, // eslint-disable-line react/forbid-prop-types
  triggers: object // eslint-disable-line react/forbid-prop-types
};

AmpAnalytics.defaultProps = {
  triggers: {}
};

export default AmpAnalytics;
